"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import {
  PublicJobAlertSubscriptionRequestSchema,
  type PublicJobAlertSubscriptionResponseDto,
} from "@artha/api-contracts";
import {
  type JobAlertFrequency,
  type JobAlertExperience,
  type PublicJobAlertStorage,
  type PublicJobAlertFeedback,
  type PublicJobAlertSubscriptionController as SharedPublicJobAlertSubscriptionController,
  JOB_ALERT_FREQUENCIES,
  JOB_ALERT_EXPERIENCES,
} from "@artha/types";

export const DEFAULT_JOB_ALERT_FREQUENCY: JobAlertFrequency = "weekly";
export const DEFAULT_JOB_ALERT_EXPERIENCE: JobAlertExperience = "fresher";
import {
  normalizePublicJobAlertUsername,
  savePublicJobAlertSubscription,
} from "../services/jobAlertClient";
import {
  readCandidateIdentity,
  writeCandidateIdentity,
} from "../../candidate-personalization/storage";

export const PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY_PREFIX =
  "public-job-alert-subscription-draft";

export const getPublicJobAlertDraftStorageKey = (username: string) =>
  `${PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY_PREFIX}:${normalizePublicJobAlertUsername(username)}`;

export type PublicJobAlertSubscriptionController =
  SharedPublicJobAlertSubscriptionController<PublicJobAlertSubscriptionResponseDto>;

const DEFAULT_STORAGE: PublicJobAlertStorage = {
  externalId: null,
  frequency: DEFAULT_JOB_ALERT_FREQUENCY,
  experience: DEFAULT_JOB_ALERT_EXPERIENCE,
};

const getBrowserTimezone = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof timezone === "string" && timezone.trim()
      ? timezone
      : undefined;
  } catch {
    return undefined;
  }
};

const readStorage = (username: string): PublicJobAlertStorage => {
  if (typeof window === "undefined") {
    return DEFAULT_STORAGE;
  }

  try {
    const raw = window.localStorage.getItem(
      getPublicJobAlertDraftStorageKey(username),
    );
    if (!raw) return DEFAULT_STORAGE;

    const parsed = JSON.parse(raw) as Partial<PublicJobAlertStorage>;
    const externalId =
      typeof parsed.externalId === "string" ? parsed.externalId : null;
    const frequency =
      parsed.frequency &&
      (JOB_ALERT_FREQUENCIES as readonly string[]).includes(parsed.frequency)
        ? (parsed.frequency as JobAlertFrequency)
        : DEFAULT_STORAGE.frequency;
    const experience =
      parsed.experience &&
      (JOB_ALERT_EXPERIENCES as readonly string[]).includes(parsed.experience)
        ? (parsed.experience as JobAlertExperience)
        : DEFAULT_STORAGE.experience;

    return {
      externalId,
      frequency,
      experience,
    };
  } catch {
    return DEFAULT_STORAGE;
  }
};

export function usePublicJobAlertSubscription(
  username: string,
  candidateEmail?: string | null,
): PublicJobAlertSubscriptionController {
  const t = useTranslations("profile");
  const posthog = usePostHog();
  const normalizedUsername = useMemo(
    () => normalizePublicJobAlertUsername(username),
    [username],
  );

  const [email, setEmail] = useState("");
  const [subscription, setSubscription] = useState<PublicJobAlertStorage>(() =>
    readStorage(normalizedUsername),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeModalCardId, setActiveModalCardId] = useState<string | null>(
    null,
  );
  const [feedback, setFeedback] = useState<PublicJobAlertFeedback>({
    cardId: null,
    errorMessage: null,
    successMessage: null,
  });

  useEffect(() => {
    setSubscription(readStorage(normalizedUsername));
  }, [normalizedUsername]);

  useEffect(() => {
    const normalizedCandidateEmail = candidateEmail?.trim();
    if (!normalizedCandidateEmail) return;
    setEmail((current) =>
      current.trim() ? current : normalizedCandidateEmail,
    );
  }, [candidateEmail]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        getPublicJobAlertDraftStorageKey(normalizedUsername),
        JSON.stringify(subscription),
      );
    } catch {
      // Ignore localStorage sync failures.
    }
  }, [normalizedUsername, subscription]);

  const setFrequency = useCallback((value: JobAlertFrequency) => {
    setSubscription((current) => ({
      ...current,
      frequency: value,
    }));
  }, []);

  const setExperience = useCallback((value: JobAlertExperience) => {
    setSubscription((current) => ({
      ...current,
      experience: value,
    }));
  }, []);

  const submit = useCallback(
    async (cardId: string) => {
      const payload = {
        email,
        frequency: subscription.frequency,
        experience: subscription.experience,
        timezone: getBrowserTimezone(),
        externalId: readCandidateIdentity().externalId || undefined,
        posthogDistinctId: posthog?.get_distinct_id?.(),
      };

      const parsedPayload =
        PublicJobAlertSubscriptionRequestSchema.safeParse(payload);

      if (!parsedPayload.success) {
        setFeedback({
          cardId,
          errorMessage: t("jobAlertInvalidEmail"),
          successMessage: null,
        });
        return null;
      }

      setIsSubmitting(true);
      setFeedback({
        cardId,
        errorMessage: null,
        successMessage: null,
      });

      try {
        const response = await savePublicJobAlertSubscription(
          normalizedUsername,
          parsedPayload.data,
        );

        setSubscription((current) => ({
          externalId: response.externalId,
          frequency: response.frequency ?? current.frequency,
          experience: response.experience ?? current.experience,
        }));
        const currentIdentity = readCandidateIdentity();
        const candidateExternalId =
          response.candidateExternalId || currentIdentity.externalId;
        writeCandidateIdentity({
          externalId: candidateExternalId,
          resumePersonalized: currentIdentity.resumePersonalized,
          candidatePreferences: currentIdentity.candidatePreferences,
        });
        setFeedback({
          cardId,
          errorMessage: null,
          successMessage: t("jobAlertSuccess"),
        });

        return response;
      } catch (error) {
        setFeedback({
          cardId,
          errorMessage:
            error instanceof Error && error.message
              ? error.message
              : t("jobAlertError"),
          successMessage: null,
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      email,
      normalizedUsername,
      posthog,
      subscription.experience,
      subscription.frequency,
      t,
    ],
  );

  return {
    email,
    frequency: subscription.frequency,
    experience: subscription.experience,
    externalId: subscription.externalId,
    isSubmitting,
    feedback,
    setEmail,
    setFrequency,
    setExperience,
    isModalOpen: activeModalCardId !== null,
    activeModalCardId,
    setActiveModalCardId,
    submit,
  };
}
